import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { db, auth, functions } from "../firebase";
import { collection, doc, setDoc, getDoc, serverTimestamp, addDoc, query, where, getDocs, onSnapshot, orderBy, limit, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useUser } from "../context/UserContext";
import { buildStorageUrl } from "../config/firebaseStorage";
import styles from "./ChatsPage.module.css";

// Deterministic chat ID from two UIDs (sorted, joined by _)
function getChatId(uid1, uid2) {
  return [uid1, uid2].sort().join("_");
}


const ChatsPage = () => {
  const { currentUser } = useUser();
  const { t } = useTranslation();
  const location = useLocation();
  const [infoOpen, setInfoOpen] = useState(false);
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersByUid, setUsersByUid] = useState({});

  // Chat creation/redirect logic (existing)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const otherUser = params.get("user");
    if (otherUser && currentUser && otherUser !== currentUser.uid && otherUser !== "undefined") {
      const myUid = currentUser.uid;
      const chatId = getChatId(myUid, otherUser);
      const chatRef = doc(db, "chats", chatId);
      (async () => {
        const chatSnap = await getDoc(chatRef);
        if (!chatSnap.exists()) {
          // Create chat doc
          await setDoc(chatRef, {
            participants: [myUid, otherUser],
            lastMessage: "",
            lastMessageSentBy: "",
            updatedAt: serverTimestamp(),
            expiresAt: null
          });
        } else {
          // If chat exists but was soft-deleted by current user, restore it
          const chatData = chatSnap.data();
          if (chatData?.deletedBy?.[myUid]) {
            await updateDoc(chatRef, {
              [`deletedBy.${myUid}`]: false,
              updatedAt: serverTimestamp()
            });
          }
        }
        navigate(`/chats/${chatId}`);
      })().catch(e => setStatus("Failed to create chat: " + e.message));
    }
  }, [location.search, navigate, currentUser?.uid]);

  // Fetch chats for current user (Real-time)
  useEffect(() => {
    if (!currentUser?.uid) {
      setChats([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const q = query(
      collection(db, "chats"),
      where("participants", "array-contains", currentUser.uid),
      orderBy("updatedAt", "desc")
    );

    const unsub = onSnapshot(q, async (snap) => {
      const chatList = snap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(chat => !chat.deletedBy?.[currentUser.uid]);
      setChats(chatList);
      setLoading(false);
    }, (err) => {
      setStatus("Failed to load chats: " + err.message);
      setLoading(false);
    });

    return () => unsub();
  }, [currentUser?.uid]);

  // Separate effect to fetch missing user info
  useEffect(() => {
    const fetchMissingUsers = async () => {
      if (!currentUser?.uid || chats.length === 0) return;

      const otherUids = Array.from(new Set(chats.flatMap(chat => chat.participants.filter(uid => uid && uid !== currentUser.uid))));
      const missingUids = otherUids.filter(uid => 
        !usersByUid[uid] || 
        (!usersByUid[uid]._attempted && !usersByUid[uid].nickname && !usersByUid[uid].username && !usersByUid[uid].deleted)
      );

      if (missingUids.length > 0) {
        // Fetch all missing user docs in parallel using getDocs with authUid query (batch)
        const userChunks = [];
        for (let i = 0; i < missingUids.length; i += 10) {
          userChunks.push(missingUids.slice(i, i + 10));
        }
        const newUsersMap = { ...usersByUid };
        let changed = false;

        // Mark as attempted to prevent infinite fetching loops for users that don't exist
        for (const uid of missingUids) {
          if (!newUsersMap[uid]) {
            newUsersMap[uid] = { _attempted: true };
          } else {
            newUsersMap[uid]._attempted = true;
          }
          changed = true;
        }

        for (const chunk of userChunks) {
          try {
            // Query users by authUid field since document IDs may be random
            const q = query(collection(db, "users"), where("authUid", "in", chunk));
            const snap = await getDocs(q);
            snap.forEach(docSnap => {
              const data = docSnap.data();
              if (data && data.authUid) {
                const existing = newUsersMap[data.authUid] || {};
                // Merge data from multiple documents (profile doc + block info doc)
                // ensuring we preserve name and pfp fields from whichever doc has them.
                newUsersMap[data.authUid] = {
                  ...existing,
                  ...data,
                  nickname: (data.nickname && typeof data.nickname === 'string' && data.nickname.trim()) ? data.nickname : existing.nickname,
                  username: (data.username && typeof data.username === 'string' && data.username.trim()) ? data.username : existing.username,
                  pfp: (data.pfp && typeof data.pfp === 'string' && data.pfp.trim()) ? data.pfp : existing.pfp
                };
                changed = true;
              }
            });
          } catch (e) {
            console.error("Error fetching user batch:", e);
          }
        }

        if (changed) {
          setUsersByUid(newUsersMap);
        }
      }
    };

    fetchMissingUsers();
  }, [chats, currentUser?.uid, usersByUid]);


  const handleChatClick = (chatId) => {
    // Navigate immediately so the UI is responsive
    navigate(`/chats/${chatId}`);

    // Mark all messages as read by updating lastReadBy asynchronously
    if (currentUser?.uid && chatId) {
      (async () => {
        try {
          const messagesRef = collection(db, "chats", chatId, "messages");
          const q = query(messagesRef, orderBy("createdAt", "desc"), limit(1));
          const snap = await getDocs(q);
          if (!snap.empty) {
            const lastMsg = snap.docs[0].data();
            if (lastMsg.createdAt) {
              await updateDoc(doc(db, "chats", chatId), {
                ["lastReadBy." + currentUser.uid]: lastMsg.createdAt
              });
            }
          }
        } catch (e) {
          // Ignore errors
        }
      })();
    }
  };

  // Dropdown menu state for chat items
  const [menuOpenId, setMenuOpenId] = useState(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (menuOpenId === null && !infoOpen) return;
    function handleClick(e) {
      // Close chat menu
      if (menuOpenId !== null) {
        if (!e.target.closest || !document.querySelector(`.${styles.dropdownMenu}`)) return;
        if (!e.target.closest(`.${styles.dropdownMenu}`) && !e.target.closest(`.${styles.menuButton}`)) {
          setMenuOpenId(null);
        }
      }
      // Close info dropdown
      if (infoOpen) {
        if (!e.target.closest(`.${styles.infoDropdown}`) && !e.target.closest(`.${styles.infoButton}`)) {
          setInfoOpen(false);
        }
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpenId, infoOpen]);

  // Block user handler
  const handleBlockUser = async (chat, otherUser, otherUid) => {
    if (!currentUser || !otherUid) return;
    setMenuOpenId(null);
    try {
      const block = httpsCallable(functions, "block");
      await block({ blockedUid: otherUid });
      alert('User blocked.');
    } catch (e) {
      alert('Failed to block user: ' + (e.message || e));
    }
  };

  // Delete chat handler (soft delete for current user)
  const handleDeleteChat = async (chat) => {
    if (!currentUser) return;
    if (!window.confirm('Are you sure you want to delete this chat? This cannot be undone.')) return;
    try {
      await updateDoc(doc(db, 'chats', chat.id), {
        [`deletedBy.${currentUser.uid}`]: true,
        updatedAt: serverTimestamp(),
      });
      // Call backend to check if chat should be scheduled for deletion
      const markForRetention = httpsCallable(functions, "markChatForRetention");
      await markForRetention({ chatId: chat.id });
      setMenuOpenId(null);
    } catch (e) {
      alert('Failed to delete chat: ' + e.message);
    }
  };

  // --------------------------------------- RETURN STATEMENT ---------------------------------
  return (
    <div className={styles.chatsPage}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', position: 'relative' }}>
        <h1 className={styles.title} style={{ margin: 0 }}>Chats</h1>
        <button 
          className={styles.infoButton}
          onClick={() => setInfoOpen(!infoOpen)}
          aria-label="Chat information"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" width={20} height={20}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
          </svg>
        </button>
        {infoOpen && (
          <div className={styles.infoDropdown} style={{ whiteSpace: 'pre-line' }}>
            {t('chats.info')}
          </div>
        )}
      </div>
      {status && <div className={styles.statusError}>{status}</div>}
      {loading ? (
        <div className={styles.chatsList}>
          {[...Array(3)].map((_, i) => (
            <div key={i} className={styles.skeletonChatItem}>
              <div className={styles.skeletonAvatar}>
                <div className={styles.shimmer} />
              </div>
              <div className={styles.skeletonMeta}>
                <div className={styles.skeletonText} style={{ width: '40%' }}>
                  <div className={styles.shimmer} />
                </div>
                <div className={styles.skeletonText} style={{ width: '75%' }}>
                  <div className={styles.shimmer} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : chats.length === 0 ? (
        <div>No chats yet.</div>
      ) : (
        <div className={styles.chatsList}>
          {chats.map(chat => {
            const otherUid = chat.participants.find(uid => uid !== currentUser?.uid);
            const userDoc = usersByUid[otherUid];

            // Fix: Some user docs may be missing or incomplete, so check for both nickname and username
            let displayName = "[deleted account]";
            if (userDoc && typeof userDoc === "object" && !userDoc.deleted) {
              if (userDoc.nickname && typeof userDoc.nickname === "string" && userDoc.nickname.trim()) {
                displayName = userDoc.nickname.trim();
              } else if (userDoc.username && typeof userDoc.username === "string" && userDoc.username.trim()) {
                displayName = userDoc.username.trim();
              }
            }
            // Fix: pfp should be fetched from userDoc if available and non-empty string
            let pfpUrl = buildStorageUrl('pfp/placeholderP.png');
            if (userDoc && !userDoc.deleted && typeof userDoc.pfp === "string" && userDoc.pfp.trim() !== "") {
              pfpUrl = userDoc.pfp.trim();
            }
            const lastRead = chat.lastReadBy?.[currentUser?.uid];
            // Use last message createdAt for unread logic
            let lastMsgCreatedAt = null;
            if (chat.lastMessage && chat.lastMessageCreatedAt) {
              lastMsgCreatedAt = chat.lastMessageCreatedAt.seconds ?
                new Date(chat.lastMessageCreatedAt.seconds * 1000) : null;
            }
            const isUnread = chat.lastMessage && 
              chat.lastMessageSentBy !== currentUser?.uid &&
              (!lastRead || (lastMsgCreatedAt && lastMsgCreatedAt > lastRead.toDate()));

            return (
              <div
                key={chat.id}
                className={styles.chatItem}
                style={{ position: 'relative' }}
              >
                <div onClick={() => handleChatClick(chat.id)} style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <img src={pfpUrl} alt={displayName} className={styles.chatAvatar}/>
                  <div className={styles.chatMeta}>
                    <span className={styles.username}>{displayName}</span>
                    <span className={`${styles.lastMessage} ${isUnread ? styles.unreadMessage : ''}`}>
                      {chat.lastMessageSentBy === currentUser?.uid ? 'You: ' : ''}
                      {chat.lastMessage ? chat.lastMessage : <span className={styles.noMessages}>(No messages yet)</span>}
                    </span>
                    {chat.updatedAt && (
                      <span className={styles.timestamp}>
                        {new Date(chat.updatedAt.seconds * 1000).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className={styles.menuButton}
                  aria-label="Open chat menu"
                  onClick={e => {
                    e.stopPropagation();
                    setMenuOpenId(menuOpenId === chat.id ? null : chat.id);
                  }}
                  style={{ marginLeft: 8 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width={26} height={26}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
                  </svg>
                </button>
                {menuOpenId === chat.id && (
                  <div className={styles.dropdownMenu} style={{ position: 'absolute', right: 12, top: 56, minWidth: 160, zIndex: 20 }}>
                    <button className={styles.menuItem} onClick={() => handleBlockUser(chat, userDoc, otherUid)} style={{ color: '#c00' }}>Block user</button>
                    <button className={styles.menuItem} onClick={() => handleDeleteChat(chat)} style={{ color: '#c00' }}>Delete chat</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ChatsPage;